import React from 'react';
import styled, { keyframes } from 'styled-components';
import Layout from '../../components/Layout';
import Container from '../../components/Container';
import Footer from '../../components/Footer';

const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(20px); }
  to { opacity: 1; transform: translateY(0); }
`;

const PageWrapper = styled.div`
  min-height: 100vh;
  width: 100%;
  background: 
    radial-gradient(circle at 20% 50%, rgba(110, 168, 255, 0.1) 0%, transparent 50%),
    radial-gradient(circle at 80% 20%, rgba(45, 108, 255, 0.1) 0%, transparent 50%),
    radial-gradient(circle at 40% 80%, rgba(58, 143, 255, 0.08) 0%, transparent 50%),
    linear-gradient(135deg, #0a0f2e 0%, #1a2451 50%, #0e1633 100%);
  position: relative;
  overflow: hidden;
  padding-bottom: 0;
`;

const BackgroundPattern = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  opacity: 0.03;
  background-image: 
    linear-gradient(45deg, #6ea8ff 25%, transparent 25%),
    linear-gradient(-45deg, #6ea8ff 25%, transparent 25%),
    linear-gradient(45deg, transparent 75%, #6ea8ff 75%),
    linear-gradient(-45deg, transparent 75%, #6ea8ff 75%);
  background-size: 60px 60px;
  background-position: 0 0, 0 30px, 30px -30px, -30px 0px;
  pointer-events: none;
`;

const PageTitle = styled.h1`
  font-size: 2.5rem;
  text-align: center;
  margin-bottom: 3rem;
  color: #fff;
  text-shadow: 0 0 16px #6ea8ff, 0 2px 10px rgba(0, 0, 0, 0.5);
  animation: ${fadeIn} 0.8s ease-out;
  
  @media (max-width: 768px) {
    font-size: 2rem;
    margin-bottom: 2rem;
  }
  
  @media (max-width: 480px) {
    font-size: 1.8rem;
    margin-bottom: 1.5rem;
  }
`;

const ContentContainer = styled.div`
  max-width: 800px;
  margin: 0 auto;
  padding: 6rem 1.5rem 4rem 1.5rem;
  position: relative;
  z-index: 2;
  animation: ${fadeIn} 1s ease-out;
  
  @media (max-width: 768px) {
    max-width: 100%;
    padding: 4rem 1.5rem 3rem 1.5rem;
  }
  
  @media (max-width: 480px) {
    padding: 3rem 1rem 2rem 1rem;
  }
`;

const Section = styled.div`
  margin-bottom: 2rem;
  background: rgba(20, 30, 60, 0.7);
  backdrop-filter: blur(12px);
  border: 2px solid rgba(45, 108, 255, 0.2);
  border-radius: 16px;
  overflow: hidden;
  transition: all 0.3s ease;
  
  &:hover {
    transform: translateY(-5px);
    box-shadow: 
      0 20px 40px rgba(45, 108, 255, 0.2),
      0 8px 16px rgba(45, 108, 255, 0.1);
    border-color: rgba(45, 108, 255, 0.4);
  }
  
  @media (max-width: 768px) {
    margin-bottom: 1.5rem;
  }
  
  @media (max-width: 480px) {
    margin-bottom: 1.2rem;
    &:hover {
      transform: translateY(-3px);
    }
  }
`;

const SectionTitle = styled.div`
  padding: 1.5rem;
  font-weight: 700;
  font-size: 1.15rem;
  color: #fff;
  background: linear-gradient(135deg, rgba(45, 108, 255, 0.2) 0%, rgba(58, 143, 255, 0.1) 100%);
  border-bottom: 1px solid rgba(45, 108, 255, 0.2);
  display: flex;
  align-items: center;
  gap: 1rem;
  
  &::before {
    content: '';
    display: block;
    width: 4px;
    height: 20px;
    background: linear-gradient(135deg, #6ea8ff 0%, #3a8fff 100%);
    border-radius: 2px;
    flex-shrink: 0;
  }
  
  @media (max-width: 768px) {
    padding: 1.2rem;
    font-size: 1rem;
  }
  
  @media (max-width: 480px) {
    padding: 1rem;
    font-size: 0.95rem;
    line-height: 1.4;
  }
`;

const SectionContent = styled.div`
  padding: 1.5rem;
  color: #b6d0ff;
  line-height: 1.6;
  
  @media (max-width: 768px) {
    padding: 1.2rem;
    line-height: 1.5;
  }
  
  @media (max-width: 480px) {
    padding: 1rem;
    font-size: 0.95rem;
  }
  
  p {
    margin-bottom: 1rem;
    
    &:last-child {
      margin-bottom: 0;
    }
  }
  
  a {
    color: #6ea8ff;
    text-decoration: none;
    transition: color 0.3s ease;
    
    &:hover {
      text-decoration: underline;
      color: #3a8fff;
    }
  }
  
  ul, ol {
    margin-left: 1.5rem;
    margin-bottom: 1rem;
    
    li {
      margin-bottom: 0.5rem;
      strong {
        color: #fff;
      }
    }
  }
  
  code {
    background: rgba(22, 32, 74, 0.8);
    color: #b6d0ff;
    padding: 0.2rem 0.4rem;
    border-radius: 4px;
    font-family: monospace;
    border: 1px solid rgba(45, 108, 255, 0.2);
  }
`;

const HowItWorks = () => {
  return (
    <Layout>
      <PageWrapper>
        <BackgroundPattern />
        <Container>
          <ContentContainer>
            <PageTitle>How Account Warmer Works</PageTitle>
            
            <Section>
              <SectionTitle>Core Functionality</SectionTitle>
              <SectionContent>
                <p>Account Warmer is a desktop application that helps users establish credibility for their online marketplace accounts through automated, natural-looking browsing patterns. The software integrates with AdsPower to create isolated browser profiles, each with unique fingerprints and session data.</p>
              </SectionContent>
            </Section>

            <Section>
              <SectionTitle>The Warming Process</SectionTitle>
              <SectionContent>
                <ol>
                  <li>
                    <strong>Profile Creation:</strong> Create isolated browser profiles with unique fingerprints using AdsPower integration. Each profile maintains its own cookies, cache, and browser history.
                  </li>
                  <li>
                    <strong>Automated Sessions:</strong> Run automated warming sessions that simulate real user behavior across multiple platforms. Our proprietary algorithm ensures each session follows natural human-like patterns.
                  </li>
                  <li>
                    <strong>Natural Interactions:</strong> Perform actions like product browsing, search queries, and natural page scrolling. The system implements variable timing between actions and realistic mouse movements to avoid detection.
                  </li>
                  <li>
                    <strong>Session Depth:</strong> Build meaningful session history through strategic browsing patterns. Each session is carefully designed to create authentic-looking user journeys while maintaining platform-specific best practices.
                  </li>
                  <li>
                    <strong>Gradual Trust Building:</strong> Build account trust over 30-90 days through consistent, authentic usage patterns. The system automatically adjusts session frequency and duration based on platform-specific requirements.
                  </li>
                </ol>
                <p style={{ marginTop: '1rem', fontStyle: 'italic' }}>
                  Note: While we can't reveal all the specific techniques used to ensure natural browsing patterns, rest assured that our system has been carefully designed to create authentic-looking user behavior that meets platform requirements.
                </p>
              </SectionContent>
            </Section>

            <Section>
              <SectionTitle>Key Features</SectionTitle>
              <SectionContent>
                <ul>
                  <li><strong>Multiple Profile Management:</strong> Create and manage multiple browser profiles with different configurations</li>
                  <li><strong>Automated Warming:</strong> Schedule and run warming sessions automatically with customizable settings</li>
                  <li><strong>Progress Tracking:</strong> Monitor warming progress and session history through detailed statistics</li>
                  <li><strong>Region-Specific Settings:</strong> Configure profiles for different geographical locations and marketplaces</li>
                </ul>
              </SectionContent>
            </Section>

            <Section>
              <SectionTitle>Technical Requirements</SectionTitle>
              <SectionContent>
                <ul>
                  <li><strong>AdsPower Integration:</strong> Requires AdsPower to be installed and running with a valid API key</li>
                  <li><strong>System Requirements:</strong> Works on Windows, macOS, and Linux with 8GB RAM minimum</li>
                  <li><strong>Network Requirements:</strong> Stable internet connection and recommended use of residential proxies</li>
                </ul>
              </SectionContent>
            </Section>

            <Section>
              <SectionTitle>Supported Countries</SectionTitle>
              <SectionContent>
                <p>Account Warmer supports the following countries and regions:</p>
                <ul>
                  <li><strong>United States (US)</strong> - amazon.com, ebay.com, vinted.com</li>
                  <li><strong>United Kingdom (UK)</strong> - amazon.co.uk, ebay.co.uk, vinted.co.uk</li>
                  <li><strong>Canada (CA)</strong> - amazon.ca, ebay.ca, vinted.ca</li>
                  <li><strong>Germany (DE)</strong> - amazon.de, ebay.de, vinted.de</li>
                  <li><strong>Netherlands (NL)</strong> - amazon.nl, ebay.nl, vinted.nl</li>
                  <li><strong>France (FR)</strong> - amazon.fr, ebay.fr, vinted.fr</li>
                  <li><strong>Spain (ES)</strong> - amazon.es, ebay.es, vinted.es</li>
                  <li><strong>Italy (IT)</strong> - amazon.it, ebay.it, vinted.it</li>
                  <li><strong>Australia (AU)</strong> - amazon.com.au, ebay.com.au, vinted.com.au</li>
                  <li><strong>India (IN)</strong> - amazon.in, ebay.in, vinted.in</li>
                  <li><strong>Ireland (IE)</strong> - amazon.ie, ebay.ie, vinted.ie</li>
                  <li><strong>Belgium (BE)</strong> - amazon.com.be, ebay.be, vinted.be</li>
                </ul>
                <p>Each region is configured with appropriate timezone, language settings, and marketplace domains to ensure authentic local browsing behavior.</p>
              </SectionContent>
            </Section>

            <Section>
              <SectionTitle>Need Help?</SectionTitle>
              <SectionContent>
                <p>If you have any questions about how Account Warmer works, our support team is here to help:</p>
                <ul>
                  <li>
                    <a href="https://t.me/deserved0" target="_blank" rel="noopener noreferrer">
                      @deserved0
                    </a>
                  </li>
                  <li>
                    <a href="https://t.me/tomnookbank" target="_blank" rel="noopener noreferrer">
                      @tomnookbank
                    </a>
                  </li>
                </ul>
              </SectionContent>
            </Section>
          </ContentContainer>
        </Container>
        <Footer />
      </PageWrapper>
    </Layout>
  );
};

export default HowItWorks; 